import { Dispatch, SetStateAction, memo } from 'react';
import { IOrder } from '../../interfaces/Order';
import { ActionIcon, Checkbox, Grid, Menu, rem } from '@mantine/core';
import moment from 'moment';
import { type MRT_PaginationState } from 'mantine-react-table';
import { useTranslation } from 'react-i18next';
import Table from '../Table';
import { formatMoneyString, getCustomerFullName } from '../../utils/strings';
import {
  IconCurrencyDollar,
  IconEdit,
  IconMenu2,
  IconPhoto,
  IconShirt,
  IconTrash,
  IconWash
} from '@tabler/icons-react';
import ConfirmationModal from '../ConfirmationModal/ConfirmationModal';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { buildOrderFolio, getOrderCardColorsByAmountDueAndStatus } from '../../utils/ordersUtils';
import { getOrderAmountDue } from '@washday/utilities';
import { IStore } from '../../interfaces/Store';
import { getAllPieces } from '../../utils/helpers/getAllPieces';
import useCanEditOrders from '../../hooks/useCanEditOrders';

const ActionMenu = ({
  order,
  searchParamStoreId,
  onOrderCleaned,
  onOrderCancelled,
  onSendEmailReceipt,
  onPrintTicket,
  showPaymentLinesModal,
  showOrderProductsModal,
  onShowOrderEvidenceModal
}) => {
  const { t } = useTranslation(['OrdersPage', 'FieldName']);
  const history = useHistory();
  const { company } = useSelector((state) => state.user);
  const canEditOrders = useCanEditOrders();

  const rowActions = [
    <Menu.Item
      onClick={() => onShowOrderEvidenceModal(order)}
      leftSection={<IconPhoto style={{ width: rem(14), height: rem(14) }} />}>
      {t('OrdersPage:evidenceModal.viewTooltip')}
    </Menu.Item>,
    <Menu.Item
      onClick={() => showPaymentLinesModal(order)}
      leftSection={<IconCurrencyDollar style={{ width: rem(14), height: rem(14) }} />}>
      {t('OrdersPage:paymentLinesModal.tooltip')}
    </Menu.Item>,
    <Menu.Item
      onClick={() => showOrderProductsModal(order)}
      leftSection={<IconShirt style={{ width: rem(14), height: rem(14) }} />}>
      {t('OrdersPage:productsModal.tooltip')}
    </Menu.Item>,
    <Menu.Item
      disabled={!canEditOrders}
      onClick={() =>
        history.push(`/orders/${order._id}?storeId=${searchParamStoreId}`, {
          storeID: searchParamStoreId
        })
      }
      leftSection={<IconEdit style={{ width: rem(14), height: rem(14) }} />}>
      {t('CommonButtons:edit')}
    </Menu.Item>,

    <Menu.Item
      onClick={() => {
        let markOutsourcedAsReturnedValue = company?.companyServicesPermissions?.enableOutsourcing;
        return ConfirmationModal({
          title: t('Notifications:CRUD.cleanedConfirmation'),
          onConfirm: () =>
            onOrderCleaned({
              sequence: order.sequence || '',
              storeId: order?.createdIn?._id || '',
              params: {
                markOutsourcedAsReturned: markOutsourcedAsReturnedValue
              }
            }),
          onCancel: () => {},
          children: company?.companyServicesPermissions?.enableOutsourcing ? (
            <Checkbox
              defaultChecked={markOutsourcedAsReturnedValue}
              label={t('Notifications:CRUD.markOutsourcedReturned')}
              onChange={(e) => {
                markOutsourcedAsReturnedValue = e.target.checked;
              }}
            />
          ) : undefined
        });
      }}
      leftSection={<IconWash style={{ width: rem(14), height: rem(14) }} />}>
      Pedido Procesado
    </Menu.Item>,
    <Menu.Item
      onClick={() =>
        ConfirmationModal({
          title: t('Notifications:CRUD.cancelConfirmation'),
          onConfirm: () =>
            onOrderCancelled({
              sequence: order.sequence || '',
              storeId: order.createdIn?._id || ''
            }),
          onCancel: () => {}
        })
      }
      leftSection={<IconTrash style={{ width: rem(14), height: rem(14) }} />}>
      Cancelar Pedido
    </Menu.Item>
  ];

  rowActions.push();

  return (
    <Menu shadow="md" width={200}>
      <Menu.Target>
        <ActionIcon variant="transparent">
          <IconMenu2 style={{ width: '70%', height: '70%' }} stroke={1.5} />
        </ActionIcon>
      </Menu.Target>

      <Menu.Dropdown>{...rowActions}</Menu.Dropdown>
    </Menu>
  );
};

export default memo(function CleaningOrderTable({
  orders = [],
  totalRowsCount = 0,
  pages = 0,
  isError = false,
  isLoading = false,
  pagination,
  setPagination,
  searchParamStoreId,
  onOrderCleaned,
  onOrderCancelled,
  onSendEmailReceipt,
  onPrintTicket,
  showPaymentLinesModal,
  showOrderProductsModal,
  onShowOrderEvidenceModal
}: {
  orders: any;
  totalRowsCount: any;
  pages: any;
  isError: boolean;
  isLoading: boolean;
  pagination: MRT_PaginationState;
  setPagination: Dispatch<SetStateAction<MRT_PaginationState>>;
  searchParamStoreId: string;
  onOrderCleaned: ({ sequence, storeId }: { sequence: string; storeId: string }) => Promise<void>;
  onOrderCancelled: ({ sequence, storeId }: { sequence: string; storeId: string }) => Promise<void>;
  onSendEmailReceipt: (orderId: string) => Promise<void>;
  onPrintTicket: (order: IOrder) => Promise<void>;
  showPaymentLinesModal: (order: IOrder) => void;
  showOrderProductsModal: (order: IOrder) => void;
  onShowOrderEvidenceModal: (order: IOrder) => void;
}) {
  const { t } = useTranslation(['FieldName']);
  const columns = [
    {
      header: t('FieldName:actions'),
      size: 50, //small column
      accessorFn: (row) => {
        return (
          <ActionMenu
            order={row}
            searchParamStoreId={searchParamStoreId}
            onOrderCleaned={onOrderCleaned}
            onOrderCancelled={onOrderCancelled}
            onSendEmailReceipt={onSendEmailReceipt}
            onPrintTicket={onPrintTicket}
            showPaymentLinesModal={showPaymentLinesModal}
            showOrderProductsModal={showOrderProductsModal}
            onShowOrderEvidenceModal={onShowOrderEvidenceModal}
          />
        );
      }
    },
    {
      accessorFn: (row) => buildOrderFolio(row.folioPrefix, row.sequence),
      header: t('FieldName:order')
    },
    {
      accessorFn: (row) => moment(row.deliveryInfo.date).format('DD/MM/YYYY hh:mm'),
      header: t('FieldName:deliveryDate')
    },
    {
      accessorFn: (row) => getCustomerFullName(row.customer.name, row.customer.lastName),
      header: t('FieldName:customer')
    },
    {
      accessorFn: (row) => {
        const orderProducts = row?.products || [];
        const orderBuyAndGerProducts = row?.buyAndGetProducts || [];
        const allPieces = getAllPieces(orderProducts, orderBuyAndGerProducts);
        return allPieces;
      },
      header: t('FieldName:pieces')
    },
    {
      accessorFn: (row) => formatMoneyString((row.total || 0).toFixed(2)),
      header: t('FieldName:total')
    },
    {
      accessorFn: (row) => formatMoneyString(getOrderAmountDue(row).toFixed(2)),
      header: t('FieldName:amountDue')
    },
    {
      accessorFn: (row) => row.notes,
      header: t('FieldName:notes')
    }
  ];

  return (
    <>
      <Grid gutter="md">
        <Grid.Col span={12}>
          <Table
            tableColumns={columns}
            pagination={pagination}
            setPagination={setPagination}
            tableData={orders}
            isError={isError}
            paginationIsLoading={isLoading}
            numPages={pages}
            totalRowsCount={totalRowsCount}
            overrideTableConfigs={{
              mantineTableBodyRowProps: ({ row }: { row: any }) => {
                const amountDue = getOrderAmountDue(row.original);
                const customColor = getOrderCardColorsByAmountDueAndStatus({
                  amountDue,
                  highlightOrderCardsByPaymentPending: (row.original?.createdIn as Partial<IStore>)
                    ?.ordersDisplayConfig?.highlightOrderCardsByPaymentPending,
                  status: row.original?.status
                });

                return {
                  style: {
                    backgroundColor: customColor?.headerBackgroundColor,
                    color: customColor?.headerTextColor
                  }
                };
              }
            }}
          />
        </Grid.Col>
      </Grid>
    </>
  );
});
